import React, { useState, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { useIntl } from 'react-intl'
import useAudioPlayer from '../hooks/useAudioPlayer'
import { useIsPhone } from '../hooks/useIsPhone'
import { CapeeshColors } from '../../../../../assets/ColorPalette'
import { playerMessages } from '../CapeeshPlayer.i18n.Messages'

const AudioPlayType = {
    DontPlay: 0,
    PlayBefore: 1,
    PlayAfter: 2,
}

const PuzzleMode = {
    Standard: 11,
    Sequential: 12,
    SequentialKeepRight: 16,
}

const useStyles = makeStyles((theme) => ({
    // ── Shared ──
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '8px 0',
    },
    questionText: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        color: '#FFFFFF',
        textAlign: 'center',
    },
    progressText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.5)',
        textAlign: 'center',
    },
    '@keyframes shake': {
        '0%, 100%': { transform: 'translateX(0)' },
        '25%': { transform: 'translateX(-6px)' },
        '50%': { transform: 'translateX(6px)' },
        '75%': { transform: 'translateX(-4px)' },
    },
    '@keyframes popOut': {
        '0%': { transform: 'scale(1)', opacity: 1 },
        '50%': { transform: 'scale(1.08)', opacity: 1 },
        '100%': { transform: 'scale(0.85)', opacity: 0 },
    },

    // ── Standard (two-column) mode ──
    columnsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        width: '100%',
        justifyContent: 'center',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        maxWidth: 220,
    },
    itemButton: {
        width: '100%',
        padding: '14px 12px',
        borderRadius: 14,
        border: '2px solid rgba(255, 255, 255, 0.25)',
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.14)',
            borderColor: 'rgba(255, 255, 255, 0.45)',
        },
    },
    itemSelected: {
        '&&': {
            borderColor: CapeeshColors.BlueBright,
            backgroundColor: 'rgba(90, 180, 241, 0.2)',
        },
        '&:hover': {
            backgroundColor: 'rgba(90, 180, 241, 0.2)',
            borderColor: CapeeshColors.BlueBright,
        },
    },
    itemMatched: {
        '&&': {
            borderColor: CapeeshColors.GreenBright,
            backgroundColor: 'rgba(128, 211, 135, 0.2)',
            opacity: 0.7,
            cursor: 'default',
        },
        '&:hover': {
            backgroundColor: 'rgba(128, 211, 135, 0.2)',
            borderColor: CapeeshColors.GreenBright,
        },
    },
    itemIncorrect: {
        '&&': {
            borderColor: CapeeshColors.PinkBright,
            backgroundColor: 'rgba(255, 102, 110, 0.2)',
            animation: '$shake 500ms ease',
        },
    },
    itemExamMatched: {
        '&&': {
            borderColor: 'rgba(255, 255, 255, 0.45)',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            opacity: 0.7,
            cursor: 'default',
        },
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderColor: 'rgba(255, 255, 255, 0.45)',
        },
    },
    itemGreenFlash: {
        '&&': {
            borderColor: CapeeshColors.GreenBright,
            backgroundColor: 'rgba(128, 211, 135, 0.25)',
            cursor: 'default',
        },
        '&:hover': {
            backgroundColor: 'rgba(128, 211, 135, 0.25)',
            borderColor: CapeeshColors.GreenBright,
        },
    },
    itemPopOut: {
        '&&': {
            animation: '$popOut 400ms ease forwards',
        },
    },
    itemWrapper: {
        overflow: 'hidden',
        maxHeight: 100,
        marginBottom: 10,
        opacity: 1,
        transition: 'max-height 350ms ease, opacity 250ms ease, margin-bottom 350ms ease',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    itemWrapperCollapsing: {
        maxHeight: 0,
        opacity: 0,
        marginBottom: 0,
    },

    // ── Sequential mode ──
    sequentialContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '8px 0',
    },
    sequentialColumnsRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        width: '100%',
        justifyContent: 'center',
    },
    promptSide: {
        flex: 1,
        maxWidth: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    promptCard: {
        width: '100%',
        boxSizing: 'border-box',
        padding: '18px 16px',
        borderRadius: 16,
        border: `2px solid ${CapeeshColors.BlueBright}`,
        backgroundColor: 'rgba(90, 180, 241, 0.15)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        textAlign: 'center',
        transition: 'opacity 350ms ease, transform 350ms ease',
    },
    answersColumn: {
        flex: 1,
        maxWidth: 220,
        display: 'flex',
        flexDirection: 'column',
    },
    answerWrapper: {
        overflow: 'hidden',
        marginBottom: 10,
        maxHeight: 100,
        opacity: 1,
        transition: 'max-height 350ms ease, opacity 250ms ease, margin-bottom 350ms ease',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    answerWrapperCollapsing: {
        maxHeight: 0,
        opacity: 0,
        marginBottom: 0,
    },
    answerButton: {
        width: '100%',
        padding: '14px 12px',
        borderRadius: 14,
        border: '2px solid rgba(255, 255, 255, 0.25)',
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 250ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.14)',
            borderColor: 'rgba(255, 255, 255, 0.45)',
        },
    },
    answerMatched: {
        '&&': {
            borderColor: CapeeshColors.GreenBright,
            backgroundColor: 'rgba(128, 211, 135, 0.2)',
            cursor: 'default',
        },
        '&:hover': {
            backgroundColor: 'rgba(128, 211, 135, 0.2)',
            borderColor: CapeeshColors.GreenBright,
        },
    },
    answerExamMatched: {
        '&&': {
            borderColor: 'rgba(255, 255, 255, 0.45)',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            cursor: 'default',
        },
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderColor: 'rgba(255, 255, 255, 0.45)',
        },
    },
    answerIncorrect: {
        '&&': {
            borderColor: CapeeshColors.PinkBright,
            backgroundColor: 'rgba(255, 102, 110, 0.2)',
            animation: '$shake 500ms ease',
        },
    },

    // ── Phone layout (stacked columns) ──
    phoneContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '4px 0',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    phoneSectionLabel: {
        fontFamily: 'Rubik',
        fontSize: 12,
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.45)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    phoneList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        maxHeight: '30vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '4px 0',
    },
    phoneItem: {
        width: '100%',
        padding: '10px 12px',
        borderRadius: 12,
        border: '2px solid rgba(255, 255, 255, 0.25)',
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:active': {
            backgroundColor: 'rgba(255, 255, 255, 0.14)',
        },
    },
    phoneSeqPrompt: {
        width: '100%',
        boxSizing: 'border-box',
        padding: '14px 12px',
        borderRadius: 12,
        border: `2px solid ${CapeeshColors.BlueBright}`,
        backgroundColor: 'rgba(90, 180, 241, 0.15)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        textAlign: 'center',
        transition: 'opacity 350ms ease, transform 350ms ease',
        flexShrink: 0,
        marginBottom: 4,
    },
    phoneSeqDivider: {
        width: '100%',
        height: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        flexShrink: 0,
    },
    phoneSeqMatchLabel: {
        fontFamily: 'Rubik',
        fontSize: 12,
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.45)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        flexShrink: 0,
    },
    phoneSeqAnswersList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '4px 0',
    },
}))

function shuffleArray(arr) {
    const items = [...arr]
    for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[items[i], items[j]] = [items[j], items[i]]
    }
    return items
}

/**
 * AssociationPuzzleInteraction — matching interaction with three modes:
 *
 *   Standard (11):              Two-column matching. Tap left, then tap right to pair.
 *   Sequential (12):           One left prompt at a time, all right options visible.
 *                               Correct match animates out and advances; incorrect shakes.
 *   SequentialKeepRight (16):  Like Sequential but right items stay available after use
 *                               and no correct/incorrect feedback (exam mode).
 */
const AssociationPuzzleInteraction = ({ testOption, onComplete, isExamMode, audioMap, onExamReady }) => {
    const classes = useStyles()
    const intl = useIntl()
    const audioPlayer = useAudioPlayer()
    const isPhone = useIsPhone()

    const alternatives = testOption.alternatives || []
    const mode = Number(testOption.scenarioInteractiveElementType) || PuzzleMode.Standard
    const audioPlayType = testOption.audioPlayType ?? AudioPlayType.DontPlay

    const isSequentialMode = mode === PuzzleMode.Sequential || mode === PuzzleMode.SequentialKeepRight
    const isKeepRight = mode === PuzzleMode.SequentialKeepRight

    const pairs = useMemo(() => {
        return alternatives
            .filter((alt) => alt.text && alt.associatedText)
            .map((alt, idx) => ({
                id: idx,
                left: alt.text,
                right: alt.associatedText,
                textAudioUrl: alt.textAudioUrl || null,
                altIndex: idx,
            }))
    }, [alternatives])

    const shuffledLeft = useMemo(() => shuffleArray(pairs.map((p) => ({ id: p.id, text: p.left }))), [pairs])
    const shuffledRight = useMemo(() => shuffleArray(pairs.map((p) => ({ id: p.id, text: p.right }))), [pairs])

    // ═══════════════════════════════
    //  Standard (two-column) state
    // ═══════════════════════════════
    const [selectedLeft, setSelectedLeft] = useState(null)
    const [matchedIds, setMatchedIds] = useState(new Set())
    const [usedLeftIds, setUsedLeftIds] = useState(new Set())
    const [usedRightIds, setUsedRightIds] = useState(new Set())
    const [incorrectFlash, setIncorrectFlash] = useState(null)
    const [answers, setAnswers] = useState([])
    const [standardExamDone, setStandardExamDone] = useState(false)
    const [greenFlashIds, setGreenFlashIds] = useState(new Set())
    const [poppingIds, setPoppingIds] = useState(new Set())
    const [stdCollapsingIds, setStdCollapsingIds] = useState(new Set())

    // ═══════════════════════════════
    //  Sequential mode state
    // ═══════════════════════════════
    const [currentLeftIdx, setCurrentLeftIdx] = useState(0)
    const [promptVisible, setPromptVisible] = useState(true)
    const [transitioning, setTransitioning] = useState(false)
    const [seqMatchedIds, setSeqMatchedIds] = useState(new Set())
    const [seqCollapsingIds, setSeqCollapsingIds] = useState(new Set())
    const [seqIncorrectId, setSeqIncorrectId] = useState(null)
    const [seqAnswers, setSeqAnswers] = useState([])
    const [seqExamDone, setSeqExamDone] = useState(false)

    // ── Standard mode handlers ──────────────────────────────────

    const handleLeftTap = useCallback(
        (item) => {
            if (isExamMode ? usedLeftIds.has(item.id) : matchedIds.has(item.id)) return
            setSelectedLeft(item)
            setIncorrectFlash(null)
        },
        [matchedIds, usedLeftIds, isExamMode]
    )

    const handleRightTap = useCallback(
        (item) => {
            if (isExamMode ? usedRightIds.has(item.id) : matchedIds.has(item.id)) return
            if (!selectedLeft) return

            const isMatch = selectedLeft.id === item.id
            const answer = {
                testOptionId: testOption.testOptionId,
                answer: `${selectedLeft.text}|${item.text}`,
                correct: isMatch,
            }

            const newAnswers = [...answers, answer]
            setAnswers(newAnswers)

            if (isExamMode) {
                const newUsedLeft = new Set(usedLeftIds)
                newUsedLeft.add(selectedLeft.id)
                setUsedLeftIds(newUsedLeft)

                const newUsedRight = new Set(usedRightIds)
                newUsedRight.add(item.id)
                setUsedRightIds(newUsedRight)

                if (isMatch) {
                    const newMatched = new Set(matchedIds)
                    newMatched.add(item.id)
                    setMatchedIds(newMatched)
                }

                setSelectedLeft(null)

                if (newUsedLeft.size >= pairs.length) {
                    setTimeout(() => {
                        setStandardExamDone(true)
                        if (onExamReady) {
                            onExamReady({
                                testOptionId: testOption.testOptionId,
                                answers: newAnswers,
                                resetFn: standardResetFn,
                            })
                        }
                    }, 400)
                }
            } else if (isMatch) {
                const matchId = item.id

                // Step 1: turn green immediately
                setGreenFlashIds((prev) => new Set(prev).add(matchId))
                setSelectedLeft(null)
                setIncorrectFlash(null)

                // Step 2: after a short pause, trigger pop-out animation
                setTimeout(() => {
                    setPoppingIds((prev) => new Set(prev).add(matchId))
                }, 450)

                // Step 3: after pop-out, collapse the wrapper and mark matched
                setTimeout(() => {
                    setMatchedIds((prev) => new Set(prev).add(matchId))
                    setStdCollapsingIds((prev) => new Set(prev).add(matchId))
                }, 850)

                // Step 4: check completion after all animations finish
                const newMatchedCount = matchedIds.size + 1
                if (newMatchedCount === pairs.length) {
                    setTimeout(() => {
                        if (onComplete) {
                            onComplete({
                                testOptionId: testOption.testOptionId,
                                answers: newAnswers,
                            })
                        }
                    }, 1300)
                }
            } else {
                setIncorrectFlash({ leftId: selectedLeft.id, rightId: item.id })
                setTimeout(() => {
                    setSelectedLeft(null)
                    setIncorrectFlash(null)
                }, 800)
            }
        },
        [selectedLeft, matchedIds, usedLeftIds, usedRightIds, answers, pairs.length, testOption.testOptionId, onComplete, isExamMode]
    )

    // ── Sequential mode handlers ────────────────────────────────

    const playMatchAudio = useCallback(
        (pairId) => {
            if (audioPlayType === AudioPlayType.DontPlay) return
            const pair = pairs.find((p) => p.id === pairId)
            if (!pair?.textAudioUrl || !audioMap) return
            const blobUrl = audioMap[pair.textAudioUrl]
            if (blobUrl) {
                audioPlayer.playAudio(blobUrl)
            }
        },
        [audioPlayType, pairs, audioMap, audioPlayer]
    )

    const advanceToNext = useCallback(
        (nextIdx, newAnswers, matchedPairId) => {
            const delay = isKeepRight ? 400 : 700

            if (nextIdx >= shuffledLeft.length) {
                setCurrentLeftIdx(nextIdx)
                if (isExamMode) {
                    setTimeout(() => {
                        if (matchedPairId != null) playMatchAudio(matchedPairId)
                        setTransitioning(false)
                        setSeqExamDone(true)
                        if (onExamReady) {
                            onExamReady({
                                testOptionId: testOption.testOptionId,
                                answers: newAnswers,
                                resetFn: seqResetFn,
                            })
                        }
                    }, delay + 100)
                } else {
                    setTimeout(() => {
                        if (matchedPairId != null) playMatchAudio(matchedPairId)
                        if (onComplete) {
                            onComplete({
                                testOptionId: testOption.testOptionId,
                                answers: newAnswers,
                            })
                        }
                    }, delay + 100)
                }
                return
            }

            setTimeout(() => {
                if (matchedPairId != null) playMatchAudio(matchedPairId)
                setCurrentLeftIdx(nextIdx)
                requestAnimationFrame(() => {
                    setPromptVisible(true)
                    setTransitioning(false)
                })
            }, delay)
        },
        [shuffledLeft.length, testOption.testOptionId, onComplete, isKeepRight, isExamMode, playMatchAudio]
    )

    const seqResetFn = useCallback(() => {
        setCurrentLeftIdx(0)
        setPromptVisible(true)
        setTransitioning(false)
        setSeqMatchedIds(new Set())
        setSeqCollapsingIds(new Set())
        setSeqIncorrectId(null)
        setSeqAnswers([])
        setSeqExamDone(false)
    }, [])

    const standardResetFn = useCallback(() => {
        setSelectedLeft(null)
        setMatchedIds(new Set())
        setUsedLeftIds(new Set())
        setUsedRightIds(new Set())
        setIncorrectFlash(null)
        setAnswers([])
        setStandardExamDone(false)
        setGreenFlashIds(new Set())
        setPoppingIds(new Set())
        setStdCollapsingIds(new Set())
    }, [])

    const handleSeqRightTap = useCallback(
        (item) => {
            if (transitioning) return

            const currentLeft = shuffledLeft[currentLeftIdx]
            if (!currentLeft) return

            const isMatch = currentLeft.id === item.id
            const answer = {
                testOptionId: testOption.testOptionId,
                answer: `${currentLeft.text}|${item.text}`,
                correct: isMatch,
            }

            const newAnswers = [...seqAnswers, answer]
            setSeqAnswers(newAnswers)

            // KeepRight (type 16): accept any answer, advance prompt, NEVER touch right items
            if (isKeepRight) {
                setTransitioning(true)
                setPromptVisible(false)
                advanceToNext(currentLeftIdx + 1, newAnswers, currentLeft.id)
                return
            }

            // Exam mode (non-KeepRight): accept any answer, collapse matched item
            if (isExamMode) {
                setTransitioning(true)
                setPromptVisible(false)

                const newMatched = new Set(seqMatchedIds)
                newMatched.add(item.id)
                setSeqMatchedIds(newMatched)

                setTimeout(() => {
                    const newCollapsing = new Set(seqCollapsingIds)
                    newCollapsing.add(item.id)
                    setSeqCollapsingIds(newCollapsing)
                }, 300)

                advanceToNext(currentLeftIdx + 1, newAnswers, currentLeft.id)
                return
            }

            // Normal mode: already matched items are not re-clickable
            if (seqMatchedIds.has(item.id)) return

            if (isMatch) {
                const newMatched = new Set(seqMatchedIds)
                newMatched.add(item.id)
                setSeqMatchedIds(newMatched)

                setTransitioning(true)
                setPromptVisible(false)

                setTimeout(() => {
                    const newCollapsing = new Set(seqCollapsingIds)
                    newCollapsing.add(item.id)
                    setSeqCollapsingIds(newCollapsing)
                }, 300)

                advanceToNext(currentLeftIdx + 1, newAnswers, currentLeft.id)
            } else {
                setSeqIncorrectId(item.id)
                setTransitioning(true)
                setTimeout(() => {
                    setSeqIncorrectId(null)
                    setTransitioning(false)
                }, 600)
            }
        },
        [transitioning, isKeepRight, isExamMode, seqMatchedIds, seqCollapsingIds, shuffledLeft, currentLeftIdx, seqAnswers, testOption.testOptionId, advanceToNext]
    )

    // ═══════════════════════════════════════════════
    //  Sequential / SequentialKeepRight rendering
    // ═══════════════════════════════════════════════
    if (isSequentialMode) {
        const currentLeft = shuffledLeft[currentLeftIdx]
        const isDone = currentLeftIdx >= shuffledLeft.length || seqExamDone

        // Phone layout: prompt stacked on top, answers below
        if (isPhone) {
            return (
                <div className={classes.phoneContainer}>
                    {testOption.text && (
                        <Typography className={classes.questionText}>{testOption.text}</Typography>
                    )}
                    <Typography className={classes.progressText}>
                        {isDone ? shuffledLeft.length : currentLeftIdx} / {shuffledLeft.length}
                    </Typography>

                    {!seqExamDone && (
                        <>
                            {!isDone && currentLeft && (
                                <div
                                    className={classes.phoneSeqPrompt}
                                    style={{
                                        opacity: promptVisible ? 1 : 0,
                                        transform: promptVisible ? 'scale(1)' : 'scale(0.95)',
                                    }}
                                >
                                    {currentLeft.text}
                                </div>
                            )}
                            <div className={classes.phoneSeqDivider} />
                            <Typography className={classes.phoneSeqMatchLabel}>{intl.formatMessage(playerMessages.matchWith)}</Typography>
                            <div className={classes.phoneSeqAnswersList}>
                                {shuffledRight.map((item) => {
                                    const isMatched = !isKeepRight && seqMatchedIds.has(item.id)
                                    const isCollapsing = !isKeepRight && seqCollapsingIds.has(item.id)
                                    const isFlashing = !isKeepRight && seqIncorrectId === item.id
                                    const suppressFeedback = isKeepRight || isExamMode

                                    let btnClass = ''
                                    if (!suppressFeedback && isMatched) btnClass = classes.answerMatched
                                    else if (isExamMode && !isKeepRight && isMatched) btnClass = classes.answerExamMatched
                                    else if (!suppressFeedback && isFlashing) btnClass = classes.answerIncorrect

                                    const wrapperClass = `${classes.answerWrapper} ${isCollapsing ? classes.answerWrapperCollapsing : ''}`

                                    return (
                                        <div key={`seq-r-${item.id}`} className={wrapperClass}>
                                            <button
                                                className={`${classes.phoneItem} ${btnClass}`}
                                                onClick={() => handleSeqRightTap(item)}
                                                disabled={isDone}
                                            >
                                                {item.text}
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </div>
            )
        }

        return (
            <div className={classes.sequentialContainer}>
                {testOption.text && (
                    <Typography className={classes.questionText}>{testOption.text}</Typography>
                )}

                <Typography className={classes.progressText}>
                    {isDone ? shuffledLeft.length : currentLeftIdx} / {shuffledLeft.length}
                </Typography>

                {!seqExamDone && (
                    <div className={classes.sequentialColumnsRow}>
                        {/* Left side — single prompt card, vertically centered */}
                        <div className={classes.promptSide}>
                            {!isDone && currentLeft && (
                                <div
                                    className={classes.promptCard}
                                    style={{
                                        opacity: promptVisible ? 1 : 0,
                                        transform: promptVisible
                                            ? 'translateX(0) scale(1)'
                                            : 'translateX(-30px) scale(0.95)',
                                    }}
                                >
                                    {currentLeft.text}
                                </div>
                            )}
                        </div>

                        {/* Right side — all answer options */}
                        <div className={classes.answersColumn}>
                            {shuffledRight.map((item) => {
                                const isMatched = !isKeepRight && seqMatchedIds.has(item.id)
                                const isCollapsing = !isKeepRight && seqCollapsingIds.has(item.id)
                                const isFlashing = !isKeepRight && seqIncorrectId === item.id
                                const suppressFeedback = isKeepRight || isExamMode

                                let btnClass = ''
                                if (!suppressFeedback && isMatched) {
                                    btnClass = classes.answerMatched
                                } else if (isExamMode && !isKeepRight && isMatched) {
                                    btnClass = classes.answerExamMatched
                                } else if (!suppressFeedback && isFlashing) {
                                    btnClass = classes.answerIncorrect
                                }

                                const wrapperClass = `${classes.answerWrapper} ${
                                    isCollapsing ? classes.answerWrapperCollapsing : ''
                                }`

                                return (
                                    <div key={`seq-r-${item.id}`} className={wrapperClass}>
                                        <button
                                            className={`${classes.answerButton} ${btnClass}`}
                                            onClick={() => handleSeqRightTap(item)}
                                            disabled={isDone}
                                        >
                                            {item.text}
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    // ═══════════════════════════════════════════════
    //  Standard (two-column) rendering
    // ═══════════════════════════════════════════════

    // Phone: stacked left items then right items
    if (isPhone) {
        return (
            <div className={classes.phoneContainer}>
                {testOption.text && (
                    <Typography className={classes.questionText}>{testOption.text}</Typography>
                )}
                <Typography className={classes.progressText}>
                    {intl.formatMessage(playerMessages.matchedProgress, { matched: isExamMode ? usedLeftIds.size : matchedIds.size, total: pairs.length })}
                </Typography>

                {!standardExamDone && (
                <>
                <Typography className={classes.phoneSectionLabel}>{intl.formatMessage(playerMessages.selectOne)}</Typography>
                <div className={classes.phoneList}>
                    {shuffledLeft.map((item) => {
                        const isUsed = isExamMode ? usedLeftIds.has(item.id) : matchedIds.has(item.id)
                        const isGreen = !isExamMode && greenFlashIds.has(item.id)
                        const isPopping = !isExamMode && poppingIds.has(item.id)
                        const isCollapsing = !isExamMode && stdCollapsingIds.has(item.id)
                        let extraClass = ''
                        if (isGreen || isPopping) {
                            extraClass = `${classes.itemGreenFlash} ${isPopping ? classes.itemPopOut : ''}`
                        } else if (isUsed) {
                            extraClass = isExamMode ? classes.itemExamMatched : classes.itemMatched
                        } else if (!isExamMode && incorrectFlash?.leftId === item.id) {
                            extraClass = classes.itemIncorrect
                        } else if (selectedLeft?.id === item.id) {
                            extraClass = classes.itemSelected
                        }

                        const wrapperClass = `${classes.itemWrapper} ${isCollapsing ? classes.itemWrapperCollapsing : ''}`

                        return (
                            <div key={`l-${item.id}`} className={wrapperClass}>
                                <button
                                    className={`${classes.phoneItem} ${extraClass}`}
                                    onClick={() => handleLeftTap(item)}
                                    disabled={isUsed || isGreen || isPopping}
                                >
                                    {item.text}
                                </button>
                            </div>
                        )
                    })}
                </div>

                <Typography className={classes.phoneSectionLabel}>{intl.formatMessage(playerMessages.matchWith)}</Typography>
                <div className={classes.phoneList}>
                    {shuffledRight.map((item) => {
                        const isUsed = isExamMode ? usedRightIds.has(item.id) : matchedIds.has(item.id)
                        const isGreen = !isExamMode && greenFlashIds.has(item.id)
                        const isPopping = !isExamMode && poppingIds.has(item.id)
                        const isCollapsing = !isExamMode && stdCollapsingIds.has(item.id)
                        let extraClass = ''
                        if (isGreen || isPopping) {
                            extraClass = `${classes.itemGreenFlash} ${isPopping ? classes.itemPopOut : ''}`
                        } else if (isUsed) {
                            extraClass = isExamMode ? classes.itemExamMatched : classes.itemMatched
                        } else if (!isExamMode && incorrectFlash?.rightId === item.id) {
                            extraClass = classes.itemIncorrect
                        }

                        const wrapperClass = `${classes.itemWrapper} ${isCollapsing ? classes.itemWrapperCollapsing : ''}`

                        return (
                            <div key={`r-${item.id}`} className={wrapperClass}>
                                <button
                                    className={`${classes.phoneItem} ${extraClass}`}
                                    onClick={() => handleRightTap(item)}
                                    disabled={isUsed || isGreen || isPopping}
                                >
                                    {item.text}
                                </button>
                            </div>
                        )
                    })}
                </div>
                </>
                )}
            </div>
        )
    }

    return (
        <div className={classes.container}>
            {testOption.text && (
                <Typography className={classes.questionText}>{testOption.text}</Typography>
            )}

            <Typography className={classes.progressText}>
                {intl.formatMessage(playerMessages.matchedProgress, { matched: isExamMode ? usedLeftIds.size : matchedIds.size, total: pairs.length })}
            </Typography>

            {!standardExamDone && (
            <div className={classes.columnsContainer}>
                {/* Left column */}
                <div className={classes.column}>
                    {shuffledLeft.map((item) => {
                        const isUsed = isExamMode ? usedLeftIds.has(item.id) : matchedIds.has(item.id)
                        const isGreen = !isExamMode && greenFlashIds.has(item.id)
                        const isPopping = !isExamMode && poppingIds.has(item.id)
                        const isCollapsing = !isExamMode && stdCollapsingIds.has(item.id)
                        let extraClass = ''
                        if (isGreen || isPopping) {
                            extraClass = `${classes.itemGreenFlash} ${isPopping ? classes.itemPopOut : ''}`
                        } else if (isUsed) {
                            extraClass = isExamMode ? classes.itemExamMatched : classes.itemMatched
                        } else if (!isExamMode && incorrectFlash?.leftId === item.id) {
                            extraClass = classes.itemIncorrect
                        } else if (selectedLeft?.id === item.id) {
                            extraClass = classes.itemSelected
                        }

                        const wrapperClass = `${classes.itemWrapper} ${isCollapsing ? classes.itemWrapperCollapsing : ''}`

                        return (
                            <div key={`l-${item.id}`} className={wrapperClass}>
                                <button
                                    className={`${classes.itemButton} ${extraClass}`}
                                    onClick={() => handleLeftTap(item)}
                                    disabled={isUsed || isGreen || isPopping}
                                >
                                    {item.text}
                                </button>
                            </div>
                        )
                    })}
                </div>

                {/* Right column */}
                <div className={classes.column}>
                    {shuffledRight.map((item) => {
                        const isUsed = isExamMode ? usedRightIds.has(item.id) : matchedIds.has(item.id)
                        const isGreen = !isExamMode && greenFlashIds.has(item.id)
                        const isPopping = !isExamMode && poppingIds.has(item.id)
                        const isCollapsing = !isExamMode && stdCollapsingIds.has(item.id)
                        let extraClass = ''
                        if (isGreen || isPopping) {
                            extraClass = `${classes.itemGreenFlash} ${isPopping ? classes.itemPopOut : ''}`
                        } else if (isUsed) {
                            extraClass = isExamMode ? classes.itemExamMatched : classes.itemMatched
                        } else if (!isExamMode && incorrectFlash?.rightId === item.id) {
                            extraClass = classes.itemIncorrect
                        }

                        const wrapperClass = `${classes.itemWrapper} ${isCollapsing ? classes.itemWrapperCollapsing : ''}`

                        return (
                            <div key={`r-${item.id}`} className={wrapperClass}>
                                <button
                                    className={`${classes.itemButton} ${extraClass}`}
                                    onClick={() => handleRightTap(item)}
                                    disabled={isUsed || isGreen || isPopping}
                                >
                                    {item.text}
                                </button>
                            </div>
                        )
                    })}
                </div>
            </div>
            )}
        </div>
    )
}

export default AssociationPuzzleInteraction
